import { useCallback, useEffect, useRef, useState } from 'react';

import { useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import {
  agentGardenApiRef,
  mapMassp4aiUseCaseInfoToDisplayData,
} from '@mercedes-benz/backstage-plugin-aiecosystem';

import { MasspUseCaseValidatorUiOptions } from './schema';

const REQUIRED_ERROR = 'Error. This is a required field.';
const VALIDATING_MESSAGE = 'Validating use case ID...';
const SUCCESS_MESSAGE = 'Success! MASSP4AI Use Case ID has been validated.';

type ValidationState =
  | 'idle'
  | 'validating'
  | 'valid'
  | 'invalid'
  | 'error'
  | 'found-but-invalid';

/**
 * Provides state and validation behavior for the MASSP4AI use case ID validator.
 * The entered use case ID is validated on demand via a Validate button and only
 * a successfully validated ID is propagated to the form data. When the field is
 * pre-populated via formData (e.g. template opened with parameters), validation
 * runs automatically once on mount.
 *
 * @param props - Field extension component props
 * @returns Use case validator state and event handlers
 */
export const useMasspUseCaseValidator = (
  props: FieldExtensionComponentProps<string, MasspUseCaseValidatorUiOptions>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    schema: {
      title = 'MASSP4AI Use Case ID',
      description = 'Link your MASSP4AI Use Case by entering its ID. This will auto-fill your agent details page with the use case name, owner, description, business need, and expected benefits.',
    },
    formData,
    uiSchema,
  } = props;

  const agentGardenApi = useApi(agentGardenApiRef);
  const [useCaseId, setUseCaseId] = useState<string>(formData ?? '');
  const [isUseCaseIdFromFormData, setIsUseCaseIdFromFormData] = useState(
    Boolean(formData?.trim()),
  );
  const [validationState, setValidationState] =
    useState<ValidationState>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [useCaseInfo, setUseCaseInfo] = useState<Record<
    string,
    { value: string; label: string }
  > | null>(null);
  const validationRequestId = useRef(0);

  const uiOptions =
    (uiSchema?.['ui:options'] as MasspUseCaseValidatorUiOptions) ?? {};
  const isEditMode = uiOptions.isEditMode ?? false;

  const hasRawErrors = rawErrors && rawErrors.length > 0;
  const isInvalid =
    hasRawErrors ||
    validationState === 'invalid' ||
    validationState === 'found-but-invalid' ||
    validationState === 'error';
  const isValid = validationState === 'valid';
  const isValidating = validationState === 'validating';
  const isFoundButInvalid = validationState === 'found-but-invalid';

  const MASSP_USE_CASE_INVALID_MESSAGE =
    'Error. The ID is invalid or incorrectly formatted.';
  const MASSP_USE_CASE_FOUND_BUT_INVALID_MESSAGE =
    'Although MASSP4AI Use Case ID has been validated, the Risk Assessment and mitigation measures have not been completed successfully. This agent cannot be onboarded until all requirements are met.';
  const MITIGATION_MEASURES_APPROVED_STATUS = 'APPROVED';

  /**
   * Propagates the trimmed input value to the scaffolder form data, or
   * undefined when the input is empty. Only successfully validated values
   * are propagated; all other paths clear the form data so the value can
   * never be submitted without a successful validation.
   *
   * @param value - The input value to propagate
   */
  const propagateValue = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      onChange(trimmedValue.length > 0 ? trimmedValue : (undefined as any));
    },
    [onChange],
  );

  /**
   * Validates the given use case ID against the Agent Garden API.
   * Only a successfully validated ID is propagated to the form data via
   * onChange; every failure path clears the form data so an invalid value
   * is never submitted and required fields cannot advance to the next step.
   * Results of requests that are superseded by a newer validation, an input
   * edit, or an unmount are discarded so stale responses cannot overwrite
   * the current state.
   *
   * @param value - The use case ID to validate
   */
  const performValidation = useCallback(
    async (value: string) => {
      const requestId = ++validationRequestId.current;
      const trimmedValue = value.trim();

      if (trimmedValue.length === 0) {
        setValidationState('error');
        setValidationError(REQUIRED_ERROR);
        setUseCaseInfo(null);
        onChange(undefined as any);
        return;
      }

      setValidationState('validating');
      setValidationError(null);
      setUseCaseInfo(null);

      try {
        const result = await agentGardenApi.getMassp4aiUseCase(trimmedValue);

        if (requestId !== validationRequestId.current) {
          return;
        }

        if (result === null) {
          setValidationState('invalid');
          setValidationError(MASSP_USE_CASE_INVALID_MESSAGE);
          onChange(undefined as any);
          return;
        }

        setUseCaseInfo(mapMassp4aiUseCaseInfoToDisplayData(result));

        // Check if risk assessment and mitigation measures are complete
        const isComplete =
          result.mitigationMeasuresScanStatus ===
          MITIGATION_MEASURES_APPROVED_STATUS;

        if (!isComplete) {
          setValidationState('found-but-invalid');
          setValidationError(MASSP_USE_CASE_FOUND_BUT_INVALID_MESSAGE);
          onChange(undefined as any);
          return;
        }

        setValidationState('valid');
        setValidationError(null);
        propagateValue(trimmedValue);
      } catch (err) {
        if (requestId !== validationRequestId.current) {
          return;
        }

        setValidationState('error');
        setValidationError(
          err instanceof Error
            ? err.message
            : 'Unknown error occurred while validating the use case ID.',
        );

        onChange(undefined as any);
      }
    },
    [agentGardenApi, onChange, propagateValue],
  );

  // Auto-populate and auto-validate when formData is present on mount
  // (e.g. template opened with the use case ID in the form parameters)
  useEffect(() => {
    if (formData && formData.trim().length > 0) {
      performValidation(formData);
    }

    return () => {
      validationRequestId.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Updates the controlled input, resets the validation result and clears the
   * form data so an edited value must be re-validated before it can be
   * submitted. For required fields this also blocks advancing to the next
   * step until the new value is successfully validated.
   *
   * @param event - Input change event
   */
  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = event.target.value;
    validationRequestId.current += 1;
    setUseCaseId(inputValue);
    setIsUseCaseIdFromFormData(false);
    setValidationState('idle');
    setValidationError(null);
    setUseCaseInfo(null);
    onChange(undefined as any);
  }

  /**
   * Triggers validation for the currently entered use case ID.
   */
  function handleValidateClick() {
    performValidation(useCaseId);
  }

  /**
   * Gets the helper text for the current validation state.
   * @returns Helper text to display below the input
   */
  function getHelperText() {
    if (validationError) {
      return validationError;
    }

    if (hasRawErrors) {
      return REQUIRED_ERROR;
    }

    if (isValidating) {
      return VALIDATING_MESSAGE;
    }

    if (isValid) {
      return SUCCESS_MESSAGE;
    }

    return description;
  }

  /**
   * Gets the helper text color for the current validation state.
   * @returns 'success.main' for valid, 'error.main' for invalid states,
   *          undefined for the default color
   */
  function getHelperTextColor(): 'success.main' | 'error.main' | undefined {
    if (isValid) {
      return 'success.main';
    }

    if (isInvalid || isFoundButInvalid) {
      return 'error.main';
    }

    return undefined;
  }

  return {
    getHelperText,
    getHelperTextColor,
    handleInputChange,
    handleValidateClick,
    isInvalid,
    isValid,
    isFoundButInvalid,
    isEditMode,
    isUseCaseIdFromFormData,
    isValidating,
    required,
    title,
    useCaseId,
    useCaseInfo,
  };
};
